import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { lucideX } from '@ng-icons/lucide';
import { hlm } from '@spartan-ng/ui-core';
import { BrnDialogCloseDirective, BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/ui-dialog-brain';
import { HlmIconComponent, provideIcons } from '@spartan-ng/ui-icon-helm';

import { HlmDialogCloseDirective } from './hlm-dialog-close.directive';

import type { ClassValue } from 'clsx';


@Component({
    selector: 'hlm-dialog-content',
    imports: [ NgComponentOutlet, BrnDialogCloseDirective, HlmDialogCloseDirective, HlmIconComponent ],
    providers: [ provideIcons({ lucideX }) ],
    host: {
        '[class]': '_computedClass()',
        '[attr.data-state]': 'state()'
    },
    template: `
        @if (component) {
            <ng-container [ngComponentOutlet]="component" />
        } @else {
            <ng-content />
        }

        @if (!$any(_dialogRef)._options.disableClose) {
            <button brnDialogClose hlm>
                <span class="tw-sr-only">Close</span>
                <hlm-icon class="tw-flex tw-w-4 tw-h-4" size="none" name="lucideX" />
            </button>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class HlmDialogContentComponent {
    protected readonly _dialogRef = inject(BrnDialogRef);
    private readonly _dialogContext = injectBrnDialogContext({ optional: true });

    public readonly state = computed(() => this._dialogRef?.state() ?? 'closed');

    public readonly component = this._dialogContext?.$component;
    private readonly _dynamicComponentClass = this._dialogContext?.$dynamicComponentClass;

    public readonly userClass = input<ClassValue>('', { alias: 'class' });
    protected readonly _computedClass = computed(() =>
        hlm(
            'tw-border-border tw-flex tw-flex-col tw-w-full tw-max-h-dvh tw-max-w-lg tw-relative tw-gap-4 tw-border tw-bg-background tw-p-6 tw-shadow-xl [tw-animation-duration:200] data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[state=closed]:tw-slide-out-to-top-[2%]  data-[state=open]:tw-slide-in-from-top-[2%] sm:tw-rounded-[1rem] md:tw-w-full',
            this.userClass(),
            this._dynamicComponentClass
        )
    );
}
